import { GraphQLClient, Variables, gql } from 'graphql-request';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { Exception } from '@wbk/logger';
import { ContentfulConfig } from '@wbk/contentful';
import type { ContentfulEvent, Experience } from '@wbk/contentful/api';

// --- TYPES & HELPERS ---

type Language = 'en-US' | 'ar-SA' | 'en-us' | 'ar-sa' | 'en' | 'ar';

export interface CommonFetchProps {
  lang?: Language | string;
  limit?: number;
  skip?: number;
  order?: string | string[];
  where?: Record<string, unknown>;
}

export type WithRateLimit<T> = T & { rateLimited: boolean };

export type GraphQLError = {
  message: string;
  extensions?: {
    contentful?: {
      code?: string;
    };
  };
};

export type GraphQLResponse = {
  response?: {
    errors?: GraphQLError[];
  };
};

export type GraphQLClientError = Error & GraphQLResponse;

const REGEIONS: Record<string, string> = {
  en: '-US',
  ar: '-SA',
  fr: '',
};

export const getContentfulSafeLocale = (locale?: string) => {
  const supportedLngs = ContentfulConfig.config?.supportLocales || [];
  const defaultLocale = supportedLngs?.[0] || 'en';

  if (!locale) {
    return defaultLocale + (REGEIONS[defaultLocale] || '');
  }

  const lang = locale?.split('-')?.[0]?.toLowerCase();
  const safeLang = supportedLngs.includes(lang) ? lang : 'en';
  const region = REGEIONS[safeLang] || '';
  const fullLang = safeLang + region;
  return fullLang;
};

export const minifyGqlQuery = (query: string) => {
  return query
    .replace(/#.*\n/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s*([{}():=@$.,])\s*/g, '$1')
    .trim();
};

const gqlClient = new GraphQLClient('');

const requestWithRateLimitHandling = async <T, V extends Variables = Variables>(
  query: string,
  variables?: V
): Promise<WithRateLimit<T>> => {
  const config = ContentfulConfig.config;
  if (!config) {
    throw new Error('ContentfulConfig was not initialized');
  }

  // Update client with current config
  gqlClient.setEndpoint(config.base);
  gqlClient.requestConfig.method = 'POST';

  try {
    const response = await gqlClient.request<T>(query, variables);
    return response as WithRateLimit<T>;
  } catch (error) {
    const graphqlError = error as GraphQLClientError;
    const isRateLimitError = graphqlError?.response?.errors?.some((e: GraphQLError) => {
      const contentful = e?.extensions?.contentful;
      return contentful?.code === 'RATE_LIMIT_EXCEEDED';
    });

    if (isRateLimitError) {
      const queryName = query?.split('(')?.[0]?.split(' ')?.[1] ?? 'Unknown Query';

      new Exception({
        error: new Error('Rate limit exceeded'),
        name: queryName,
        context: {
          extra: { query, variables },
        },
      });

      return { rateLimited: true } as WithRateLimit<T>;
    }

    throw error;
  }
};

// --- QUERIES ---

export const IMAGE_QUERY = `
title
sys{
  id
  publishedAt
}
url
width
height
contentType
`;

export const LOCATION_QUERY = `
title
address
city
countryCode
seactionHeader
googlePlaceName
location { lat lon }
banner {${IMAGE_QUERY}}
accessibility
`;

export const SCHEDULE_QUERY = `
title
openTitle
openDateTime
closeDateTime
openScheduleText
`;

export const RICHTEXT_QUERY = `
json
links {
  assets {
    block {${IMAGE_QUERY}}
  }
}`;

export const SEO_QUERY = `
title
description
keywords
image {${IMAGE_QUERY}}
noindex
`;

export const TAG_QUERY = `
 title
 subtitle
 slug
 id
 background{${IMAGE_QUERY}}
 icon{${IMAGE_QUERY}}
`;

export const LINK_QUERY = `
id
label
href
deeplinkHref
theme
shape
isExternal
iconPosition
icon {${IMAGE_QUERY}}
image {${IMAGE_QUERY}}`;

export const EVENT_LIST_QUERY = `
id
title
subtitle
image11{${IMAGE_QUERY}}
image32{${IMAGE_QUERY}}
image31{${IMAGE_QUERY}}
slug
zone{id slug zoneLogo{${IMAGE_QUERY}} sponsorLogo{${IMAGE_QUERY}}}
startingPrice
currencyCode
buttonLabel
cardButtonLabel
schedule{${SCHEDULE_QUERY}}
location{${LOCATION_QUERY}}
eventType
cmsTagsCollection(limit:5){ items {id} }
`;

export const AUCTION_LIST_QUERY = `
${EVENT_LIST_QUERY}
startAt
endAt
upfrontPayment
`;

export const OTHER_BOOKING_OPTIONS_QUERY = `
id
title
subtitle
renderType
itemRenderType
sectionBackground {${IMAGE_QUERY}}
logo {${IMAGE_QUERY}}
webLinksCollection(limit: 2){items{${LINK_QUERY}}}
contentCollection(limit: 25) {
  items {
    __typename
      ... on Event {
      ${`${EVENT_LIST_QUERY}
      isStreamingEvent
      buyLiveStream{id}
      buttonIcon{${IMAGE_QUERY}}
      streamingMessage`}}
    ... on Experience {${EVENT_LIST_QUERY}}
    ... on Shows {${EVENT_LIST_QUERY}}
    ... on Restaurant {${EVENT_LIST_QUERY}}
    ... on Auction {${AUCTION_LIST_QUERY}}
  }
}
`;

// --- API ACTIONS ---

const EVENT_QUERY = gql`
query getEventDetail($lang: String, $limit: Int, $skip: Int, $where: EventFilter, $order: [EventOrder]) {
  eventCollection(locale: $lang, limit: $limit, skip: $skip, where: $where, order: $order) {
    total
    items {
      __typename
      sys { id }
      id
      title
      subtitle
      description{${RICHTEXT_QUERY}}
      eventLogo{${IMAGE_QUERY}}
      slug
      ticketingUrlSlug
      image11{${IMAGE_QUERY}}
      image31{${IMAGE_QUERY}}
      image32{${IMAGE_QUERY}}
      startingPrice
      currencyCode
      schedule{${SCHEDULE_QUERY}}
      isStreamingEvent
      zoneEntryIncluded
      streamingUrl 
      buttonLabel
      cardButtonLabel
      eventType
      authGaurd
      buttonLink
      cmsTagsCollection(limit: 10){
        items {${TAG_QUERY}}
      }
      buyLiveStream{
        id
        title
        description
        currencyCode
        startingPrice
        taxInfo
        limitations
        layout
        button {
          title
          type
          label
          link
        }
      }
      carousalCollection(limit: 60) {
        items {${IMAGE_QUERY}}
      }
      artistsCollection(limit: 10) {
        items {
          name
          thumbnail {${IMAGE_QUERY}}
          subtitle
        }
      }
      season{title slug}
      zone{
        id slug title zoneLogo{${IMAGE_QUERY}} sponsorLogo{${IMAGE_QUERY}} 
      }
      location{${LOCATION_QUERY}}
      category{id title slug}
      sponsorsCollection(limit: 20) {
        items {
          id
          title
          titleColor
          sponsorsCollection(limit: 20) {
            items {
              id
              title
              logo {${IMAGE_QUERY}}
              link
            }
          }
        }
      }
      isComingSoon
      richTerms { json }
      whatToKnow
      specialPromotion
      showResellBanner
      seo{${SEO_QUERY}}
      otherBookingSection {${OTHER_BOOKING_OPTIONS_QUERY}}
      organizationSlug
      visibility
    }
  }
}
`;

const EXPERIENCE_QUERY = gql`
  query getExperienceDetail($lang: String, $limit: Int, $skip: Int, $where: ExperienceFilter, $order: [ExperienceOrder]) {
    experienceCollection(locale: $lang, limit: $limit, skip: $skip, where: $where, order: $order) {
      total
      items {
        __typename
        sys { id }
        id
        title
        subtitle
        description{${RICHTEXT_QUERY}}
        slug
        ticketingUrlSlug
        image11{${IMAGE_QUERY}}
        image31{${IMAGE_QUERY}}
        startingPrice
        discountedPrice
        currencyCode
        schedule{${SCHEDULE_QUERY}} 
        buttonLabel
        cardButtonLabel        
        zoneEntryIncluded
        eventType
        authGaurd
        buttonLink
        carousalCollection(limit: 40) {
          items {${IMAGE_QUERY}}
        }
        cmsTagsCollection(limit: 10){
          items {${TAG_QUERY}}
        }
        season{title slug}
        zone{
          id slug title zoneLogo{${IMAGE_QUERY}} sponsorLogo{${IMAGE_QUERY}}
        }
        location{${LOCATION_QUERY}}
        category{id title slug}
        sponsorsCollection(limit: 20) {
          items {
            id
            title
            titleColor
            sponsorsCollection(limit: 20) {
              items {
                id
                title
                logo {${IMAGE_QUERY}}
                link
              }
            }
          }
        }
        isComingSoon
        richTerms { json }
        whatToKnow
        specialPromotion
        ticketingUrlSlug
        showResellBanner
        socialLinks {
          id
          title
          iconLinksCollection(limit: 10) {
            items {${LINK_QUERY}}
          }
        }
        seo{${SEO_QUERY}}
        otherBookingSection {${OTHER_BOOKING_OPTIONS_QUERY}}
        visibility
      }
    }
  }
`;

export const getEventDetail = async (props: CommonFetchProps) => {
  try {
    const res = await requestWithRateLimitHandling<{
      eventCollection: WithRateLimit<{ items: ContentfulEvent[]; total: number }>;
    }>(minifyGqlQuery(EVENT_QUERY), {
      order: 'order_ASC',
      ...props,
      lang: getContentfulSafeLocale(props.lang),
    });

    return {
      data: res.eventCollection?.items?.[0],
      rateLimited: res.rateLimited ?? false,
    };
  } catch (error) {
    throw new Exception({ error, name: 'getEventDetailException' });
  }
};

export const useGetEventDetail = (
  props: CommonFetchProps,
  options?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEventDetail>>>>
) => {
  return useQuery({
    ...options,
    queryKey: ['getEventDetail', props],
    queryFn: () => getEventDetail(props),
  });
};

export const getExperienceDetail = async (props: CommonFetchProps) => {
  try {
    const res = await requestWithRateLimitHandling<{
      experienceCollection: WithRateLimit<{ items: Experience[]; total: number }>;
    }>(minifyGqlQuery(EXPERIENCE_QUERY), {
      order: 'order_ASC',
      ...props,
      lang: getContentfulSafeLocale(props.lang),
    });

    return {
      data: res.experienceCollection?.items?.[0],
      rateLimited: res.rateLimited ?? false,
    };
  } catch (error) {
    throw new Exception({ error, name: 'getExperienceDetailException' });
  }
};

export const useGetExperienceDetail = (
  props: CommonFetchProps,
  options?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getExperienceDetail>>>>
) => {
  return useQuery({
    queryKey: ['getExperienceDetail', props],
    queryFn: () => getExperienceDetail(props),
    ...options,
  });
};
